import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { GRAINS, type Grain, buildBuckets, changeOf, rangeFor } from '../charts';
import { today } from '../dates';
import type { Workout } from '../types';
import { TimeChart } from './TimeChart';

type Props = { onError: (err: unknown) => void };

/** 検証済みの配色 (dark surface #16181b で全チェック PASS) */
const COLOR = {
  volume: '#ff4d17',
  bodyWeight: '#3987e5',
  bodyFat: '#199e70',
} as const;

/** ツールチップ用: kg のまま桁区切り */
const fmtKg = (v: number) => Math.round(v).toLocaleString('ja-JP');

/**
 * 縦軸用: 軸の最大値で単位と小数桁を決めて、目盛り間で表記を揃える
 * (「10t」と「5.0t」が混ざらないようにする)
 */
const fmtVolumeTick = (v: number, axisMax: number) => {
  if (axisMax < 1000) return String(Math.round(v));
  const digits = axisMax >= 20000 ? 0 : 1;
  return `${(v / 1000).toFixed(digits)}t`;
};

const fmt1 = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));

const signed = (v: number) => `${v > 0 ? '+' : ''}${fmt1(v)}`;

export function ChartView({ onError }: Props) {
  const [grain, setGrain] = useState<Grain>('day');
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);

  const to = today();

  useEffect(() => {
    let cancelled = false;
    const { from } = rangeFor(grain, to);

    setLoading(true);
    api
      .listWorkouts(from, to)
      .then((list) => {
        if (!cancelled) setWorkouts(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [grain, to, onError]);

  const buckets = useMemo(() => buildBuckets(grain, workouts, to), [grain, workouts, to]);

  const volumeTotal = buckets.reduce((s, b) => s + b.volumeKg, 0);
  const trainedDays = buckets.reduce((s, b) => s + b.trainedDays, 0);
  const weightChange = changeOf(buckets, (b) => b.bodyWeight);
  const fatChange = changeOf(buckets, (b) => b.bodyFat);

  const grainLabel = GRAINS.find((g) => g.id === grain)!.label;

  return (
    <div className="charts">
      <div className="grainpick" role="tablist" aria-label="集計の粒度">
        {GRAINS.map((g) => (
          <button
            key={g.id}
            className="chip"
            role="tab"
            aria-selected={grain === g.id}
            aria-pressed={grain === g.id}
            onClick={() => setGrain(g.id)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading" style={{ minHeight: 260 }}>
          <span className="spinner" />
        </div>
      ) : (
        <>
          <TimeChart
            title="トレーニング重量"
            unit="kg"
            color={COLOR.volume}
            kind="bar"
            points={buckets.map((b) => ({ bucket: b, value: b.volumeKg }))}
            format={fmtKg}
            formatTick={fmtVolumeTick}
            headline={{
              value: `${Math.round(volumeTotal).toLocaleString('ja-JP')} kg`,
              sub: `${grainLabel} · 実施 ${trainedDays}日`,
            }}
          />

          <TimeChart
            title="体重"
            unit="kg"
            color={COLOR.bodyWeight}
            kind="line"
            points={buckets.map((b) => ({ bucket: b, value: b.bodyWeight }))}
            format={fmt1}
            headline={
              weightChange
                ? {
                    value: `${fmt1(weightChange.last)} kg`,
                    sub: `期間内 ${signed(weightChange.delta)} kg`,
                  }
                : undefined
            }
          />

          <TimeChart
            title="体脂肪率"
            unit="%"
            color={COLOR.bodyFat}
            kind="line"
            points={buckets.map((b) => ({ bucket: b, value: b.bodyFat }))}
            format={fmt1}
            headline={
              fatChange
                ? {
                    value: `${fmt1(fatChange.last)} %`,
                    sub: `期間内 ${signed(fatChange.delta)} pt`,
                  }
                : undefined
            }
          />

          <p className="hint">
            重量は{grainLabel}の合計、体重と体脂肪率は{grainLabel}の平均です。
            計測していない{grain === 'day' ? '日' : '期間'}は線を繋がず空けています。
          </p>
        </>
      )}
    </div>
  );
}
